<template>
  <div class="free-form-attribute-row">
    <component
      :is="attributeRowComponent"
      :key="props.attribute.code || 'empty'"
      :attribute="valueAttribute"
      :showValidation="props.showValidation"
      :disabled="!props.attribute.code"
      showDeleteButton
      @change="$emit('change', $event)"
      @remove="$emit('remove')"
    >
      <template #name>
        <a-form-model-item
          class="free-form-attribute-row__name"
          :validateStatus="showNameError ? 'error' : undefined"
          :help="showNameError ? $t('validations.required_field') : undefined"
        >
          <InfiniteScrollSelect
            ref="attributeSelectRef"
            :value="props.attribute.code"
            :defaultOptions="props.attribute.code ? [props.attribute] : []"
            :placeholder="`${$t('offers.create_form_placeholders.start_typing_to_search')}`"
            :customRequest="fetchProductAttributes"
            labelKey="public_name"
            valueKey="code"
            onFocusFetch
            @change="selectAttribute"
          >
            <template #options="{ options }">
              <a-select-option
                v-for="option in options"
                :key="option.code"
                :value="option.code"
                :disabled="props.unavailableCodes.includes(option.code)"
              >
                {{ getAttributeLabel(option) }}
              </a-select-option>
            </template>
            <template #extraButton>
              <a-button
                type="link"
                block
                icon="plus"
                class="free-form-attribute-row__create"
                @click="showCreateAttributeModal = true"
              >
                {{ $t("offers.create_new_attribute") }}
              </a-button>
            </template>
          </InfiniteScrollSelect>
        </a-form-model-item>
      </template>
    </component>
    <CreateAttributeModal
      :visible="showCreateAttributeModal"
      @close="showCreateAttributeModal = false"
      @submit="$emit('change', $event)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"

import InfiniteScrollSelect from "@/components/Common/InfiniteScrollSelect/index.vue"
import { fetchProductAttributes } from "@/modules/offers/api/productModeration"
import type { ProductAttributeSelectHandle } from "@/modules/offers/types/createOffer"
import type { ProductModerationAttribute } from "@/modules/offers/types/offers"

import { getAttributeLabel } from "../../utils/attributeLabels"

import AttributeInputRow from "./AttributeInputRow.vue"
import AttributeSelectRow from "./AttributeSelectRow.vue"
import CreateAttributeModal from "./CreateAttributeModal.vue"

type Props = {
  attribute: ProductModerationAttribute
  unavailableCodes: string[]
  showValidation?: boolean
}
type Emits = {
  (event: "change", attribute: ProductModerationAttribute): void
  (event: "remove"): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const attributeSelectRef = ref<ProductAttributeSelectHandle>()
const showCreateAttributeModal = ref(false)

const attributeRowComponent = computed(() =>
  props.attribute.is_defined_list ? AttributeSelectRow : AttributeInputRow
)
const valueAttribute = computed(() => ({ ...props.attribute, is_required: true }))
const showNameError = computed(() => props.showValidation && !props.attribute.code)

const selectAttribute = (code?: string) => {
  if (!code) return emit("change", { value: [] })

  const attribute = attributeSelectRef.value.selectOptions.find((item) => item.code === code)
  emit("change", { ...attribute, value: [] })
}
</script>

<style lang="scss" scoped>
.free-form-attribute-row {
  &__name {
    flex: 0 1 440px;
    min-width: 0;
    margin: 0;
  }

  &__create {
    padding: 0 8px;
    text-align: left;
  }

  ::v-deep .attribute-list-record {
    align-items: flex-start;
  }

  ::v-deep .attribute-list-record__value {
    flex: 0 1 285px;
    min-width: 0;
    margin-left: 0;
  }

  ::v-deep .attribute-list-record__numeric-value {
    flex-basis: 370px;
  }

  ::v-deep .attribute-list-record__action {
    flex: 0 0 32px;
    margin-left: 0;
  }

  ::v-deep .ant-form-item-control {
    line-height: 32px;
  }

  ::v-deep .ant-form-item-control-wrapper {
    width: 100%;
  }
}
</style>
